import { createContext, useContext, type ReactNode } from 'react';

/**
 * Tenant context for white-label help center mode.
 *
 * Populated by `functions/_middleware.ts` (Cloudflare Pages Function) which
 * injects `window.__AUCTISYNC_TENANT__` for requests to a tenant custom
 * domain. Platform mode (help.auctisync.com) leaves the global undefined and
 * this context returns null.
 *
 * Module #33 white-label chunk 1.
 */

export interface TenantBranding {
  logo_url?: string;
  primary_color?: string;
  secondary_color?: string;
  support_email?: string;
  main_site_url?: string;
}

export interface TenantContextValue {
  id: string;
  name: string;
  display_name: string | null;
  tier: string;
  branding: TenantBranding;
  custom_domain_apex: string;
  help_origin: string;
}

const TenantContext = createContext<TenantContextValue | null>(null);

declare global {
  interface Window {
    __AUCTISYNC_TENANT__?: TenantContextValue;
  }
}

function readTenantFromWindow(): TenantContextValue | null {
  if (typeof window === 'undefined') return null;
  const blob = window.__AUCTISYNC_TENANT__;
  if (!blob || typeof blob !== 'object' || typeof blob.id !== 'string') return null;
  // Defensive normalization — branding may be missing on legacy injections.
  return {
    ...blob,
    branding: blob.branding ?? {},
  };
}

export function TenantProvider({ children }: { children: ReactNode }) {
  const tenant = readTenantFromWindow();
  return <TenantContext.Provider value={tenant}>{children}</TenantContext.Provider>;
}

/**
 * Hook returning the current tenant context, or null in platform mode.
 *
 * Call sites should branch on `tenant === null` rather than throwing —
 * platform-mode rendering at help.auctisync.com is a first-class state, not
 * an error.
 */
export function useTenant(): TenantContextValue | null {
  return useContext(TenantContext);
}

/**
 * Convenience: tenant's preferred display name with fallback chain.
 * "Discount Tool Auctions" if display_name set, else name, else "AuctiSync".
 */
export function tenantDisplayName(tenant: TenantContextValue | null): string {
  if (!tenant) return 'AuctiSync';
  return tenant.display_name ?? tenant.name;
}
